/**
 * The web half of the `user` data type (docs/plans/element-sets-and-me.md,
 * "Why a User data type"; keepr-api utils/userElements.js is the canonical
 * module): the small, pure questions every surface asks about a user
 * element and its value.
 *
 *   value      the account's user id string, or — with `options.allowMultiple`
 *              — a sorted list of them; '' / [] is empty
 *   identity   `options.identity: true` on ONE user element per card's
 *              effective list makes it the card's ACCOUNT LINK: the API
 *              denormalises `items.accountId` from it, one item per account
 *              per collection, and that is what `me` — the KQL keyword, the
 *              `/me` endpoint, quick add's `{{me.*}}` — resolves through
 *   ME_VALUE   the raw value the builder, the chips and the pickers pass
 *              around for "the signed-in caller"; `kqlBuilder.makeValueNode`
 *              turns it into the grammar's `me` node (never a string)
 *
 * Pure: no React, no antd, no HTTP.
 */
import { normalizeId } from './ids';

export const USER_DATA_TYPE = 'user';

/** The raw value that stands for the signed-in caller in pickers and rows. */
export const ME_VALUE = 'me';

const HEX_ID = /^[0-9a-fA-F]{24}$/;

/** '' / [] / null: nobody picked. */
export const isEmptyUserValue = value => value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length);

/** Is this a 24-hex account (or item) id? */
export const isHexId = value => typeof value === 'string' && HEX_ID.test(value.trim());

export const isUserElement = element => element?.dataType === USER_DATA_TYPE;

export const allowsMultipleUsers = element => isUserElement(element) && element?.options?.allowMultiple === true;

/** Is this element a card's account link? (A multi element never is; the API refuses the pair.) */
export const isIdentityElement = element => (
	isUserElement(element) && element?.options?.identity === true && element?.options?.allowMultiple !== true
);

/** The ids a stored user value holds (string or list), de-duplicated, as strings. */
export function userIdsOf(value) {
	const raw = Array.isArray(value) ? value : [value];
	const out = [];
	raw.forEach(entry => {
		const id = typeof entry === 'string' ? entry.trim() : normalizeId(entry);
		if (id && HEX_ID.test(id) && !out.includes(id)) out.push(id);
	});
	return out;
}

/**
 * The value a user element stores for a picker's answer: one id, or a
 * sorted, de-duplicated list with `allowMultiple` — the API's own
 * canonical spelling, so the form never sends a shape the write path
 * re-orders.
 */
export function normalizeUserValue(raw, element) {
	if (allowsMultipleUsers(element)) {
		const ids = userIdsOf(Array.isArray(raw) ? raw : (raw ? [raw] : []));
		return [...ids].sort();
	}
	if (Array.isArray(raw)) return userIdsOf(raw)[0] || '';
	return userIdsOf(raw)[0] || '';
}

/**
 * The account link of an element list — the LAST definition of a name
 * wins, the item write path's merge rule — or null.
 */
export function identityElementOf(elements = []) {
	const byName = new Map();
	(elements || []).forEach(el => {
		if (el && typeof el.name === 'string' && el.name) byName.set(el.name, el);
	});
	for (const el of byName.values()) {
		if (isIdentityElement(el)) return el;
	}
	return null;
}

/** The user elements of a merged list, last definition of a name winning. */
export function userElementsOf(elements = []) {
	const byName = new Map();
	(elements || []).forEach(el => {
		if (!el || !el.name) return;
		if (isUserElement(el)) byName.set(el.name, el);
		else byName.delete(el.name); // retyped by a descendant
	});
	return [...byName.values()];
}

/**
 * The cards of a collection whose effective list carries an account link —
 * the cards a LINKED ITEM can be of — each with the element that links it:
 * `[{ card, element }]`. `cardIds` restricts to the collection's member
 * cards (null = every card the resolver knows).
 */
export function identityCardsOf({ cards = [], cardIds = null, getElementsForCard } = {}) {
	const out = [];
	(cards || []).forEach(card => {
		const id = normalizeId(card?._id);
		if (!id) return;
		if (cardIds && !cardIds.has(id)) return;
		const elements = (typeof getElementsForCard === 'function' ? getElementsForCard(id) : card?.elements) || [];
		const element = identityElementOf(elements);
		if (element) out.push({ card, element });
	});
	return out;
}

/**
 * The elements `{{me.<element>}}` may read: the merged elements of every
 * identity-carrying card, FIRST definition of a name winning (the KQL
 * vocabulary's rule; keepr-api utils/quickAddTemplates/validate.js
 * loadMeElements). Empty when no card carries a link.
 */
export function meElementsOf({ cards = [], cardIds = null, getElementsForCard } = {}) {
	const out = new Map();
	identityCardsOf({ cards, cardIds, getElementsForCard }).forEach(({ card }) => {
		const elements = (typeof getElementsForCard === 'function' ? getElementsForCard(normalizeId(card?._id)) : card?.elements) || [];
		elements.forEach(el => {
			if (el && el.name && !out.has(el.name)) out.set(el.name, el);
		});
	});
	return out;
}

/** A public-projection user as the platform names them: fullName, else first + last, else username. */
export function userDisplayName(user) {
	if (!user) return '';
	const full = typeof user.fullName === 'string' ? user.fullName.trim() : '';
	if (full) return full;
	const parts = [user.firstName, user.lastName].map(v => (typeof v === 'string' ? v.trim() : '')).filter(Boolean);
	if (parts.length) return parts.join(' ');
	return typeof user.username === 'string' ? user.username.trim() : '';
}
